import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile } from 'node:fs/promises'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'

const execFileAsync = promisify(execFile)

/** Launcher for Aider — AI pair programming in your terminal */
export class AiderLauncher implements ProviderLauncher {
  readonly name = 'aider'
  readonly displayName = 'Aider'
  readonly pricingHint = '🆓 Gratuit (open-source, clé API personnelle)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('aider', ['--version'])
      return true
    } catch {
      return false
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('aider', ['--version'])
      return stdout.trim()
    } catch {
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt = config.systemPromptContent ?? await readFile(config.systemPromptPath, 'utf-8')

    const args: string[] = []

    // Set system prompt via chat language file so Aider enters interactive mode
    // with the interview instructions pre-loaded in its context.
    // NOTE: --message disables interactive chat mode (one-shot only).
    // Instead write system instructions via --read.
    args.push('--read', config.systemPromptPath)

    if (config.model) {
      args.push('--model', config.model)
    }

    if (config.permissionMode === 'bypassPermissions') {
      args.push('--no-auto-commits')
      args.push('--yes-always')
    } else if (config.permissionMode === 'acceptEdits') {
      args.push('--no-auto-commits')
    } else {
      args.push('--no-auto-commits')
    }

    args.push('.')

    // Suppress Aider's own system prompt so only ours is effective
    const env = { ...process.env, AIDER_SYSTEM_PROMPT: systemPrompt }

    const recorder = new SessionRecorder()

    const ptyProc = getPty().spawn(await resolveBinaryOrFallback('aider'), args, {
      name: 'xterm-256color',
      cwd: workdir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env,
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(({ exitCode, signal }) => {
            cleanup()
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
      },
      recorder,
    }
  }
}
